import React from "react"
import {
  faBuilding,
  faChartLine,
  faClockRotateLeft,
  faMotorcycle,
  faReceipt,
  faSackDollar,
  faShop,
  faSitemap,
  faTriangleExclamation,
  faTruck,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { MetricCard } from "../../../components/MetricCard/MetricCard"
import { AdminRuntimeStats } from "../../../Objects/AdminDashboard/AdminRuntimeStats"

interface ServerRuntimeSectionProps {
  runtimeStats: AdminRuntimeStats
}

export const ServerRuntimeSection: React.FC<ServerRuntimeSectionProps> = ({ runtimeStats }) => {
  const toGb = (bytes: number) => `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`

  const uptimeMinutes = Math.floor(runtimeStats.processUptimeSeconds / 60)

  const formatPercent = (value: number) => `${value.toFixed(1)}%`

  const statusClass = (status: string) =>
    status === "Crítico" || status === "Erro"
      ? "bg-red-100 text-red-700"
      : status === "Atenção"
        ? "bg-amber-100 text-amber-700"
        : "bg-green-100 text-green-700"

  return (
    <div className="w-full overflow-auto bg-white h-100vh p-1 md:p-0">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-2 p-1">
        <MetricCard title="RAM do processo" value={toGb(runtimeStats.processWorkingSetBytes)} icon={faChartLine} />

        <MetricCard
          title="RAM total do servidor"
          value={toGb(runtimeStats.totalPhysicalMemoryBytes)}
          icon={faBuilding}
        />

        <MetricCard title="Disco total (uploads)" value={toGb(runtimeStats.storageTotalBytes)} icon={faBuilding} />

        <MetricCard title="Disco livre (uploads)" value={toGb(runtimeStats.storageFreeBytes)} icon={faSackDollar} />

        <MetricCard title="Disco usado (uploads)" value={toGb(runtimeStats.storageUsedBytes)} icon={faChartLine} />

        <MetricCard
          title="Uso do disco (uploads)"
          value={formatPercent(runtimeStats.storageUsagePercent)}
          icon={faSackDollar}
        />

        <MetricCard
          title="RAM livre do servidor"
          value={toGb(runtimeStats.availablePhysicalMemoryBytes)}
          icon={faSackDollar}
        />

        <MetricCard
          title="Uso da RAM servidor"
          value={formatPercent(runtimeStats.physicalMemoryUsagePercent)}
          icon={faSackDollar}
        />

        <MetricCard
          title="Uso da RAM processo"
          value={formatPercent(runtimeStats.processMemoryUsagePercent)}
          icon={faChartLine}
        />

        <MetricCard
          title="CPU processo (média)"
          value={formatPercent(runtimeStats.processCpuUsagePercent)}
          icon={faChartLine}
        />

        <MetricCard title="Núcleos de CPU" value={`${runtimeStats.processorCount}`} icon={faBuilding} />

        <MetricCard title="Threads do processo" value={`${runtimeStats.processThreadCount}`} icon={faBuilding} />

        <MetricCard title="Uptime do processo" value={`${uptimeMinutes} min`} icon={faReceipt} />

        <MetricCard
          title="Operadores ativos (5 min)"
          value={`${runtimeStats.estimatedActiveOperatorsLast5Minutes}`}
          icon={faUserGroup}
        />
        <MetricCard title="Pedidos em fluxo" value={`${runtimeStats.openOrdersInFlow}`} icon={faTruck} />

        <MetricCard title="Pedidos pendentes" value={`${runtimeStats.pendingOrdersInFlow}`} icon={faReceipt} />

        <MetricCard title="Pedidos em preparo" value={`${runtimeStats.preparingOrdersInFlow}`} icon={faShop} />

        <MetricCard title="Pedidos em entrega" value={`${runtimeStats.inDeliveryOrdersInFlow}`} icon={faMotorcycle} />
      </div>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="mt-3 mx-1 rounded-2xl border-2 border-orange-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-orange-100 p-2 text-orange-600">
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </span>

            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Estado geral e alertas</h3>

              <p className="text-[11px] text-slate-500">Visão consolidada dos principais riscos operacionais.</p>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-600">
            Máquina: <span className="font-semibold">{runtimeStats.machineName || "-"}</span> • SO:{" "}
            <span className="font-semibold">{runtimeStats.operatingSystem || "-"}</span>
          </p>

          <p className="mt-1 text-xs text-slate-600">
            Volume monitorado: <span className="font-semibold">{runtimeStats.storageMountPoint || "-"}</span>
          </p>

          {runtimeStats.alerts.length === 0 ? (
            <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-xs font-semibold text-green-700">
              Sem alertas críticos no momento.
            </p>
          ) : (
            <ul className="mt-3 space-y-2 text-xs text-red-700">
              {runtimeStats.alerts.map((alert, index) => (
                <li key={`${alert}-${index}`} className="rounded-lg bg-red-50 px-3 py-2 leading-relaxed">
                  {alert}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
        <div className="mt-3 mx-1 rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-blue-100 p-2 text-blue-600">
                <FontAwesomeIcon icon={faSitemap} />
              </span>

              <div>
                <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Filas e workers</h3>

                <p className="text-[11px] text-slate-500">Backlog, sucesso, falhas e saúde das filas.</p>
              </div>
            </div>

            <span className="text-xs text-slate-500">
              Atualizado em:{" "}
              {runtimeStats.capturedAtUtc ? new Date(runtimeStats.capturedAtUtc).toLocaleTimeString() : "-"}
            </span>
          </div>

          <div className="mt-4 overflow-auto rounded-xl border border-slate-100">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="border-b text-center text-slate-500">
                  <th className="py-2 font-bold pr-2">Fila</th>
                  <th className="py-2 font-bold pr-2">Descrição</th>
                  <th className="py-2 font-bold pr-2">Aguardando</th>
                  <th className="py-2 font-bold pr-2">Processando</th>
                  <th className="py-2 font-bold pr-2">Total enfileirado</th>
                  <th className="py-2 font-bold pr-2">Total processado</th>
                  <th className="py-2 font-bold pr-2">Sucesso</th>
                  <th className="py-2 font-bold pr-2">Erro</th>
                  <th className="py-2 font-bold pr-2">Saúde</th>
                </tr>
              </thead>

              <tbody>
                {runtimeStats.queues.map((item) => (
                  <tr
                    key={item.queueKey || item.queueName}
                    className="border-b last:border-0 text-center hover:bg-slate-50/60"
                  >
                    <td className="py-2 pr-2 font-bold text-slate-700">{item.queueName}</td>
                    <td className="py-2 pr-2 text-center text-slate-600">{item.description}</td>
                    <td className="py-2 pr-2">{item.waitingCount}</td>
                    <td className="py-2 pr-2">{item.processingCount}</td>
                    <td className="py-2 pr-2">{item.totalEnqueued}</td>
                    <td className="py-2 pr-2">{item.totalDequeued}</td>
                    <td className="py-2 pr-2 text-emerald-700 font-semibold">{item.totalSucceeded}</td>
                    <td className="py-2 pr-2 text-rose-700 font-semibold">{item.totalFailed}</td>
                    <td className="py-2 pr-2">
                      <span
                        className={`rounded-full px-2 py-1 text-[11px] font-bold ${statusClass(item.healthStatus)}`}
                      >
                        {item.healthStatus}
                      </span>
                      {item.healthMessage && (
                        <div className="mt-1 text-[10px] text-slate-500">{item.healthMessage}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
        <div className="mt-3 mx-1 rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-violet-100 p-2 text-violet-600">
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </span>

            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Timers</h3>

              <p className="text-[11px] text-slate-500">Saúde das rotinas agendadas e detecção de travamentos.</p>
            </div>
          </div>

          <div className="mt-4 overflow-auto rounded-xl border border-slate-100">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="border-b text-center text-slate-500">
                  <th className="py-2 font-bold pr-2">Timer</th>
                  <th className="py-2 font-bold pr-2">Descrição</th>
                  <th className="py-2 font-bold pr-2">Período</th>
                  <th className="py-2 font-bold pr-2">Último início</th>
                  <th className="py-2 font-bold pr-2">Última conclusão</th>
                  <th className="py-2 font-bold pr-2">Duração</th>
                  <th className="py-2 font-bold pr-2">Erro</th>
                  <th className="py-2 font-bold pr-2">Saúde</th>
                </tr>
              </thead>

              <tbody>
                {runtimeStats.timers.map((item) => (
                  <tr
                    key={item.timerKey || item.timerName}
                    className="border-b last:border-0 text-center hover:bg-slate-50/60"
                  >
                    <td className="py-2 pr-2 font-bold text-slate-700">{item.timerName}</td>
                    <td className="py-2 pr-2 text-center text-slate-600">{item.description}</td>
                    <td className="py-2 pr-2">{item.period || item.dueTime}</td>
                    <td className="py-2 pr-2">
                      {item.lastStartedAtUtc ? new Date(item.lastStartedAtUtc).toLocaleString() : "-"}
                    </td>
                    <td className="py-2 pr-2">
                      {item.lastCompletedAtUtc ? new Date(item.lastCompletedAtUtc).toLocaleString() : "-"}
                    </td>
                    <td className="py-2 text-black pr-2">{item.lastDurationMs ? `${item.lastDurationMs} ms` : "-"}</td>
                    <td className="py-2 pr-2 text-red-600">{item.lastError || "-"}</td>
                    <td className="py-2 pr-2">
                      <span
                        className={`rounded-full px-2 py-1 text-[11px] font-bold ${statusClass(item.healthStatus)}`}
                      >
                        {item.healthStatus}
                      </span>
                      {(item.isStalled || item.healthMessage) && (
                        <div className="mt-1 text-[10px] text-slate-500">
                          {item.healthMessage || "Timer sem execução recente."}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Animate>
    </div>
  )
}
